import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { Button } from "@/components/ui/button";

/**
 * OAuth 2.1 consent route for the DrSullySpeaks MCP server.
 * Mounted at /.lovable/oauth/consent. Supabase redirects here after an
 * external MCP client (ChatGPT, Claude, etc.) starts an authorization
 * flow. Unauthenticated visitors are sent to /auth?next=<full consent URL>
 * so they return here with the same authorization_id after signing in.
 */

// The Supabase JS client exposes supabase.auth.oauth as a beta namespace whose
// types aren't published yet; wrap it locally so callers stay typed.
type AuthorizationDetails = {
  client?: { name?: string; redirect_uri?: string } | null;
  requested_scope?: string;
  redirect_url?: string;
  redirect_to?: string;
};
type OAuthResult<T> = { data: T | null; error: { message: string } | null };
type OAuthNamespace = {
  getAuthorizationDetails: (id: string) => Promise<OAuthResult<AuthorizationDetails>>;
  approveAuthorization: (id: string) => Promise<OAuthResult<{ redirect_url?: string; redirect_to?: string }>>;
  denyAuthorization: (id: string) => Promise<OAuthResult<{ redirect_url?: string; redirect_to?: string }>>;
};
const oauth = () =>
  (supabase.auth as unknown as { oauth: OAuthNamespace }).oauth;

export default function OAuthConsent() {
  const [params] = useSearchParams();
  const authorizationId = params.get("authorization_id") ?? "";
  const [details, setDetails] = useState<AuthorizationDetails | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!authorizationId) {
        setError("Missing authorization_id in the request URL.");
        return;
      }
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        // Preserve the FULL consent URL so /auth returns the user back here.
        const next = window.location.pathname + window.location.search;
        window.location.href = "/auth?next=" + encodeURIComponent(next);
        return;
      }
      setEmail(sess.session.user.email ?? null);
      const { data, error } = await oauth().getAuthorizationDetails(authorizationId);
      if (!active) return;
      if (error) {
        setError(error.message);
        return;
      }
      // If the provider returned an immediate redirect result, follow it.
      const immediate = data?.redirect_url ?? data?.redirect_to;
      if (immediate && !data?.client) {
        window.location.href = immediate;
        return;
      }
      setDetails(data);
    })();
    return () => {
      active = false;
    };
  }, [authorizationId]);

  async function decide(approve: boolean) {
    setBusy(true);
    setError(null);
    const { data, error } = approve
      ? await oauth().approveAuthorization(authorizationId)
      : await oauth().denyAuthorization(authorizationId);
    if (error) {
      setBusy(false);
      setError(error.message);
      return;
    }
    const target = data?.redirect_url ?? data?.redirect_to;
    if (!target) {
      setBusy(false);
      setError("No redirect returned by the authorization server.");
      return;
    }
    window.location.href = target;
  }

  const clientName = details?.client?.name ?? "an app";
  const redirectUri = details?.client?.redirect_uri;
  const scopes = (details?.requested_scope ?? "openid email profile").split(/\s+/).filter(Boolean);

  return (
    <>
      <PageMeta title="Authorize app — DrSullySpeaks" description="Approve or deny an external app's request to connect to your DrSullySpeaks account." path="/.lovable/oauth/consent" />
      <SiteNav />
      <main className="mx-auto max-w-lg px-6 py-16">
        <h1 className="font-display text-2xl md:text-3xl mb-2">
          Connect {clientName} to DrSullySpeaks
        </h1>
        <p className="text-sm text-muted-foreground mb-6">
          This lets {clientName} call this app's enabled tools while you are signed in.
        </p>

        {error && (
          <div className="mb-6 rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
            {error}
          </div>
        )}

        {!details && !error && (
          <p className="text-sm text-muted-foreground">Loading authorization request…</p>
        )}

        {details && (
          <div className="space-y-6">
            {email && (
              <div className="rounded-md border border-border/60 bg-card/40 p-4 text-sm">
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Signed in as</div>
                <div className="font-medium">{email}</div>
              </div>
            )}
            <div className="rounded-md border border-border/60 bg-card/40 p-4 text-sm space-y-2">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">App</div>
                <div className="font-medium">{clientName}</div>
                {redirectUri && (
                  <div className="text-xs text-muted-foreground mt-1 break-all">Redirect: {redirectUri}</div>
                )}
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Requested access</div>
                <ul className="list-disc pl-5 space-y-1">
                  {scopes.map((s) => (
                    <li key={s}>
                      {s === "openid" || s === "profile"
                        ? "Share your basic profile"
                        : s === "email"
                        ? "Share your email address"
                        : `Additional permission requested: ${s}`}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-xs text-muted-foreground pt-2 border-t border-border/40">
                This does not bypass this app's permissions or backend policies.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button disabled={busy} onClick={() => decide(true)}>
                {busy ? "Working…" : `Approve ${clientName}`}
              </Button>
              <Button disabled={busy} variant="ghost" onClick={() => decide(false)}>
                Cancel connection
              </Button>
            </div>
          </div>
        )}
      </main>
      <SiteFooter />
    </>
  );
}
